<template>
  <v-container fluid grid-list-lg>
    <v-layout wrap justify-center>
      <v-flex md10 class="mt-4">
        <div class="title mb-2">
          Number of new users daily
          <span v-if="visibleSignupYearLabel" class="signup-stats-year">{{ visibleSignupYearLabel }}</span>
        </div>
        <div class="signup-stats-wrapper">
          <v-btn icon @click="prevSignupPage" :disabled="signupWindowStart === 0">
            <v-icon>chevron_left</v-icon>
          </v-btn>
          <div class="signup-stats-scroll">
            <div
              v-for="bucket in visibleSignupBuckets"
              :key="bucket.date"
              class="signup-stats-cell"
              :class="{
                'signup-stats-low-verify': bucket.count > 0 && bucket.otp_verified_count / bucket.count < 0.5,
              }"
            >
              <div class="signup-stats-date">{{ formatSignupDate(bucket.date) }}</div>
              <div class="signup-stats-count">{{ bucket.count }}</div>
              <div class="signup-stats-verified">OTP Verified: {{ bucket.otp_verified_count }}</div>

              <!-- For each day card, show up to three top countries with their signup count. 
              If more than three countries signed up that day, show "+N more" — hovering that shows the full list of remaining countries. 
              Days with zero signups skip this block entirely (the v-if check). -->
              <div v-if="verifiedCountries(bucket).length > 0" class="signup-stats-countries">
                <div class="signup-stats-section-label">OTP Verified:</div>
                <div v-for="c in verifiedCountries(bucket).slice(0, 3)" :key="'v-' + c.prefix">
                  {{ c.prefix }} {{ c.country }}: {{ c.verified }}
                </div>
                <div
                  v-if="verifiedCountries(bucket).length > 3"
                  class="signup-stats-more"
                  :title="
                    verifiedCountries(bucket)
                      .slice(3)
                      .map((c) => c.prefix + ' ' + c.country + ': ' + c.verified)
                      .join(', ')
                  "
                >
                  +{{ verifiedCountries(bucket).length - 3 }} more
                </div>
              </div>

              <!-- unverified -->
              <div v-if="unverifiedCountries(bucket).length > 0" class="signup-stats-countries">
                <div class="signup-stats-section-label">Not verified:</div>
                <div v-for="c in unverifiedCountries(bucket).slice(0, 3)" :key="'u-' + c.prefix">
                  {{ c.prefix }} {{ c.country }}: {{ c.unverified }}
                </div>
                <div
                  v-if="unverifiedCountries(bucket).length > 3"
                  class="signup-stats-more"
                  :title="
                    unverifiedCountries(bucket)
                      .slice(3)
                      .map((c) => c.prefix + ' ' + c.country + ': ' + c.unverified)
                      .join(', ')
                  "
                >
                  +{{ unverifiedCountries(bucket).length - 3 }} more
                </div>
              </div>
              <div
                v-if="bucket.count > 0 && bucket.otp_verified_count / bucket.count < 0.5"
                class="signup-stats-warning"
              >
                Possible bot signups: over half didn't verify
              </div>
            </div>
          </div>
          <v-btn icon @click="nextSignupPage" :disabled="signupWindowStart + signupPageSize >= signupBuckets.length">
            <v-icon>chevron_right</v-icon>
          </v-btn>
        </div>
      </v-flex>
      <v-flex md10 class="mt-4">
        <div class="title mb-2">Active users by country</div>
        <v-data-table
          :headers="countryStatsHeaders"
          :items="countryStatsWithRowNumbers"
          :items-per-page="10"
          :loading="countryStatsLoading"
          :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
          sort-by="total"
          :sort-desc="true"
          class="elevation-1"
        >
          <template v-slot:item.active_pct="{ item }"> {{ item.active_pct }}% </template>
          <template v-slot:item.last_appointment="{ item }">
            {{ item.last_appointment || '—' }}
          </template>
          <template v-slot:header.total="{ header }">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on" class="header-with-tooltip">
                  {{ header.text }}
                </span>
              </template>
              <span>Total verified-phone patients from this country prefix. Excludes providers.</span>
            </v-tooltip>
          </template>
          <template v-slot:header.active="{ header }">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on" class="header-with-tooltip">
                  {{ header.text }}
                </span>
              </template>
              <span>Patients who have at least one appointment record (any status, including cancelled).</span>
            </v-tooltip>
          </template>
          <template v-slot:header.inactive="{ header }">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on" class="header-with-tooltip">
                  {{ header.text }}
                </span>
              </template>
              <span>Patients who registered and verified their phone but never made an appointment.</span>
            </v-tooltip>
          </template>
          <template v-slot:header.active_pct="{ header }">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on" class="header-with-tooltip">
                  {{ header.text }}
                </span>
              </template>
              <span>Active divided by total, as a percentage. Higher = more engaged users from this country.</span>
            </v-tooltip>
          </template>
          <template v-slot:header.last_appointment="{ header }">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on" class="header-with-tooltip">
                  {{ header.text }}
                </span>
              </template>
              <span
                >Most recent appointment created by any patient from this country. Helps gauge whether users are still
                active or fully dormant.</span
              >
            </v-tooltip>
          </template>
        </v-data-table>
      </v-flex>
      <v-flex row align-center md10 class="title mt-4">
        <div>User roles</div>
        <v-icon class="icon mx-2" color="blue" @click="addUserRoleClick">add_circle</v-icon>
      </v-flex>
      <v-flex md10>
        <v-text-field v-model="searchInput" label="Search by email, user ID, or role"></v-text-field>
        <div class="mb-2">Found {{ filteredUsers.length }} users</div>
        <v-simple-table>
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left">ID</th>
                <th class="text-left">User</th>
                <th class="text-left">Role</th>
                <th class="text-left">Action</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="user in paginatedUsers" :key="user.id">
                <td>{{ user.id }}</td>
                <td>{{ user.email }}</td>
                <td width="50%">
                  <v-select v-model="user.roles" :items="roles" multiple></v-select>
                </td>
                <td>
                  <v-btn :disabled="!isDirty(user.id)" @click="saveUserRoles(user.id)">Save</v-btn>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>

        <!-- render 7 page buttons only e.g. 1 2 3 ... 18 19 20 ("..." is a button) -->
        <v-pagination
          v-model="userRolesPage"
          :length="userRolesPageCount"
          :total-visible="7"
          class="mt-3"
        ></v-pagination>
      </v-flex>
    </v-layout>
    <v-dialog scrollable v-model="isAddUserRoleOpen" width="400px">
      <v-card>
        <v-card-title>Add User Role</v-card-title>
        <v-card-text>
          <v-text-field v-model="addUserRoleUserId" label="User ID"></v-text-field>
          <v-select v-model="addUserRoleUserRoles" :items="roles" multiple></v-select>
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="() => (isAddUserRoleOpen = false)"> Cancel </v-btn>
          <v-btn text @click="() => addUserRole()" color="blue"> Save </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { Vue } from 'vue-property-decorator';
import store from '../../store';
import roleService from '../../services/roles';
import usersService from '../../services/users';

export default Vue.extend({
  store,
  data: () => ({
    roles: [],
    originalRoles: [],
    users: [],
    existingUsers: [],
    isAddUserRoleOpen: false,
    addUserRoleUserId: null,
    addUserRoleUserRoles: [],
    searchInput: '',
    userRolesPage: 1,
    userRolesPerPage: 5,
    searchTerm: '',
    signupBuckets: [],
    countryStats: [],
    countryStatsLoading: false,
    signupWindowStart: 0,
    signupPageSize: 7,
    signupSpikeThreshold: 100,
  }),
  computed: {
    filteredUsers() {
      if (!this.searchTerm) {
        return this.users;
      }
      const searchTerm = this.searchTerm.toLowerCase();
      return this.users.filter((user) => {
        const emailMatch = user.email && user.email.toLowerCase().includes(searchTerm);
        const idMatch = user.id.toString().includes(searchTerm);
        const roleMatch = user.roles.some((role) => role.toLowerCase().includes(searchTerm));
        return emailMatch || idMatch || roleMatch;
      });
    },

    // what the table will render
    paginatedUsers() {
      const start = (this.userRolesPage - 1) * this.userRolesPerPage;
      return this.filteredUsers.slice(start, start + this.userRolesPerPage);
    },

    // the || 1 guard prevents Vuetify from getting a 0 page count when search returns nothing, which would hide the control entirely
    userRolesPageCount() {
      return Math.ceil(this.filteredUsers.length / this.userRolesPerPage) || 1;
    },
    visibleSignupBuckets() {
      return this.signupBuckets.slice(this.signupWindowStart, this.signupWindowStart + this.signupPageSize);
    },
    visibleSignupYearLabel() {
      if (this.visibleSignupBuckets.length === 0) return '';
      const years = new Set(this.visibleSignupBuckets.map((b) => b.date.slice(0, 4)));
      const sorted = Array.from(years).sort();
      if (sorted.length === 1) return sorted[0];
      return `${sorted[0]}–${sorted[sorted.length - 1]}`;
    },
    countryStatsHeaders() {
      return [
        { text: '#', value: 'rowNumber', align: 'right', sortable: false, width: 50 },
        { text: 'Prefix', value: 'prefix', align: 'left' },
        { text: 'Country', value: 'country', align: 'left' },
        { text: 'Total', value: 'total', align: 'right' },
        { text: 'Active', value: 'active', align: 'right' },
        { text: 'Inactive', value: 'inactive', align: 'right' },
        { text: 'Active %', value: 'active_pct', align: 'right' },
        { text: 'Last appointment', value: 'last_appointment', align: 'left' },
      ];
    },
    countryStatsWithRowNumbers() {
      return this.countryStats.map((item, index) => ({
        ...item,
        rowNumber: index + 1,
      }));
    },
  },
  watch: {
    searchInput() {
      this.userRolesPage = 1; // Reset to page 1 when the search changes ; Otherwise a user on page 3 who searches for something with fewer results sees an empty table
      this.debouncedSearch();
    },
  },
  created() {
    this.debouncedSearch = this.debounce(this.setSearchTerm, 500);
  },
  mounted() {
    this.fetchUserRoles();
    this.fetchSignupStats();
    this.fetchCountryStats();
    roleService.getRoles().then((roles) => {
      this.originalRoles = roles;
      this.roles = roles.map((role) => role.name);
    });
  },
  methods: {
    debounce(func, delay) {
      let timeoutId;
      return (...args) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          func.apply(this, args);
        }, delay);
      };
    },
    setSearchTerm() {
      this.searchTerm = this.searchInput;
    },
    fetchUserRoles() {
      roleService.getUserRoles().then((users) => {
        const groupUserRoles = {};
        users.forEach((user) => {
          const userId = user.user_id;
          const userEmail = user.user_email;
          const roleName = user.name;

          if (groupUserRoles[userId]) {
            groupUserRoles[userId].roles.push(roleName);
          } else {
            groupUserRoles[userId] = {
              id: userId,
              email: userEmail,
              roles: [roleName],
            };
          }
        });

        this.existingUsers = JSON.parse(JSON.stringify(Object.values(groupUserRoles)));
        this.users = Object.values(groupUserRoles);
      });
    },
    async saveUserRoles(userId) {
      if (!this.isDirty(userId)) {
        return;
      }

      const current = this.users.filter((u) => u.id === userId)[0].roles;
      const original = this.existingUsers.filter((u) => u.id === userId)[0].roles;

      const toAdd = current.filter((role) => !original.includes(role));
      const toRemove = original.filter((role) => !current.includes(role));

      try {
        await Promise.all(
          toAdd.map((role) => {
            const roleId = this.originalRoles.find((r) => r.name === role).id;
            return roleService.addUserRole(userId, roleId);
          })
        );
        await Promise.all(
          toRemove.map((role) => {
            const roleId = this.originalRoles.find((r) => r.name === role).id;
            return roleService.deleteUserRole(userId, roleId);
          })
        );

        this.fetchUserRoles();
        this.$notify({
          title: 'Success',
          text: `Successfully update roles.`,
          type: 'success',
        });
        this.$emit('success');
      } catch (error) {
        console.log(error);
      }
    },
    addUserRoleClick() {
      this.isAddUserRoleOpen = true;
    },
    async addUserRole() {
      try {
        await Promise.all(
          this.addUserRoleUserRoles.map((role) => {
            const roleId = this.originalRoles.find((r) => r.name === role).id;
            return roleService.addUserRole(this.addUserRoleUserId, roleId);
          })
        );
        this.isAddUserRoleOpen = false;

        this.fetchUserRoles();
        this.$notify({
          title: 'Success',
          text: `Successfully update roles.`,
          type: 'success',
        });
        this.$emit('success');
      } catch (error) {
        console.error('Error adding user role:', error);
      }
    },
    isDirty(userId) {
      const current = this.users.filter((u) => u.id === userId)[0].roles;
      const original = this.existingUsers.filter((u) => u.id === userId)[0].roles;
      return !this.arraysEqual(current, original);
    },
    arraysEqual(arrA, arrB) {
      return arrA.length === arrB.length && arrA.every((value, index) => value === arrB[index]);
    },
    async fetchSignupStats() {
      const end = new Date();
      const start = new Date('2016-01-01');

      const toIctDateString = (d) => {
        // Convert a JS Date (UTC) to a Bangkok 'YYYY-MM-DD' string
        const ict = new Date(d.getTime() + 7 * 60 * 60 * 1000);
        return ict.toISOString().slice(0, 10);
      };

      try {
        const data = await usersService.signupStats(toIctDateString(start), toIctDateString(end));
        this.signupBuckets = data;
        // Default window: show the most recent 7 days
        this.signupWindowStart = Math.max(0, data.length - this.signupPageSize);
      } catch (err) {
        console.error('Failed to load signup stats', err);
      }
    },
    async fetchCountryStats() {
      this.countryStatsLoading = true;
      try {
        this.countryStats = await usersService.countryStats();
      } catch (err) {
        console.error('Failed to load country stats', err);
      } finally {
        this.countryStatsLoading = false;
      }
    },
    formatSignupDate(isoDate) {
      // 'YYYY-MM-DD' → 'D MMM' (e.g. '27 May')
      const d = new Date(isoDate);
      return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    },
    verifiedCountries(bucket) {
      if (!bucket.by_country) return [];
      return bucket.by_country.filter((c) => c.verified > 0);
    },
    unverifiedCountries(bucket) {
      if (!bucket.by_country) return [];
      return bucket.by_country.filter((c) => c.unverified > 0);
    },
    prevSignupPage() {
      this.signupWindowStart = Math.max(0, this.signupWindowStart - this.signupPageSize);
    },
    nextSignupPage() {
      this.signupWindowStart = Math.min(
        this.signupBuckets.length - this.signupPageSize,
        this.signupWindowStart + this.signupPageSize
      );
    },
  },
});
</script>

<style scoped>
/*
 * Cell colors:
 *   white = normal day
 *   red   = low OTP verify rate (<50% of new signups verified their phone).
 *           Likely signature of bot mass-signup or SMS pumping.
 */
.signup-stats-warning {
  font-size: 10px;
  color: #c62828;
  margin-top: 6px;
  border-top: 1px solid #ef9a9a;
  padding-top: 4px;
  line-height: 1.2;
}
.signup-stats-year {
  font-size: 14px;
  font-weight: normal;
  color: #888;
  margin-left: 8px;
}
.signup-stats-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
}
.signup-stats-scroll {
  display: flex;
  flex: 1;
  overflow-x: auto;
  gap: 6px;
}
.signup-stats-cell {
  flex: 0 0 auto;
  width: 120px;
  padding: 8px;
  text-align: center;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: #fafafa;
}
.signup-stats-cell.signup-stats-low-verify {
  background: #ffebee;
  border-color: #ef5350;
}
.signup-stats-verified {
  font-size: 10px;
  color: #555;
  margin-top: 6px;
  border-top: 1px solid #e0e0e0;
  padding-top: 4px;
}
.signup-stats-date {
  font-size: 11px;
  color: #666;
}
.signup-stats-count {
  font-size: 18px;
  font-weight: 600;
  margin-top: 4px;
}
.signup-stats-countries {
  font-size: 10px;
  color: #555;
  margin-top: 6px;
  border-top: 1px solid #e0e0e0;
  padding-top: 4px;
  line-height: 1.4;
  text-align: left;
}
.signup-stats-more {
  color: #888;
  font-style: italic;
  cursor: help;
}
.signup-stats-section-label {
  font-size: 9px;
  font-weight: 600;
  color: #777;
  margin-top: 4px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.header-with-tooltip {
  cursor: help;
  border-bottom: 1px dotted #999;
}
</style>
